import { SettingOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client/react';
import { Button, Col, Dropdown, Row, Tabs, Tag, Tooltip } from 'antd';
import clsx from 'clsx';
import { findIndex, includes, isNaN, values } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../AppContext';
import Error404 from '../../../../Error404';
import {
  ForwardArrow,
  InfoIcon,
  ProjectIcon,
  Timezone,
} from '../../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../../common/CommonUseLazyQuery';
import {
  ACCESS_LEVEL,
  ACCESS_TYPE,
  ADDONS,
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  BREAKPOINTS,
  CHECKLIST_STATUS_LABEL,
  CONFIRMATION_TYPES,
  GA_EVENT,
  GA_LABEL,
  REGEX,
  REQUEST_FEATURE_UPGRADE_KEYS,
  ROUTES,
  TAB_KEYS,
} from '../../../../common/constants';
import {
  getProjectMeMode,
  getProjectTeamFilter,
  hasProjectBeenInitialized,
  migrateToProjectSpecificStorage,
  setProjectMeMode,
  setProjectTeamFilter,
} from '../../../../common/projectFilterStorage';
import { Event } from '../../../../common/trackEvents';
import useRouter from '../../../../common/useRouter';
import { titleCase } from '../../../../common/utils';
import CanPerform from '../../../../components/CanPerform';
import CommonDropdown from '../../../../components/CommonDropdown';
import CommonPopover from '../../../../components/CommonPopover';
import HasAccess from '../../../../components/HasAccess';
import RestrictedAccess from '../../../../components/RestrictedAccess';
import UpgradeModal from '../../../../components/UpgradeModal';
import history from '../../../../historyData';
import { CREATE_PROJECT_RFI_DRAFT } from '../../graphql/Mutation';
import {
  GET_SINGLE_PROJECT,
  MY_PROJECT_AGENCY_DROPDOWN_LIST,
} from '../../graphql/Queries';
import ReportConfigModal from '../ReportConfigModal';
import Team from './pages/agencies/Agency';
import UnassignAgency from './pages/agencies/UnassignAgency';
import Eqc from './pages/eqc/Eqc';
import EqcType from './pages/eqcTypes/EqcTypes';
import Instruction from './pages/instructions/Instruction';
import Nomenclature from './pages/nomentclature/Nomenclature';
import PlanAndTrack from './pages/planAndTrack/PlanAndTack';
import Register from './pages/register/Register';
import ConfirmationModal from './pages/rfi/ConfirmationModal';
import RfiActivationRequiredModal from './pages/rfi/RfiActivationRequiredModal';
import User from './pages/users/User';

const ProjectDetails = () => {
  const { getCurrentUser, getTenantUser, dispatch } = useContext(AppContext);
  const currentUser = getCurrentUser();
  const {
    navigate,
    location,
    params: { projectId, tab, registerId },
  } = useRouter();
  const navFilter = location?.state?.navFilter;
  const navPagination = location?.state?.navPagination;
  const [showReportConfigModal, setShowReportConfigModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [featureType, setFeatureType] = useState();
  const [showRfiActivationRequiredModal, setShowRfiActivationRequiredModal] =
    useState(false);
  const [confirmationType, setConfirmationType] = useState(
    CONFIRMATION_TYPES?.EDIT,
  );
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [alreadyDraftedUserData, setAlreadyDraftedUserData] = useState();
  const [showModal, setShowModal] = useState(false);
  const currentUserId = getCurrentUser()?.id || {};
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [isMeMode, setIsMeMode] = useState(() => {
    // Initialize with the correct project's me mode state
    return projectId ? getProjectMeMode(projectId) : false;
  });
  const [hasInitializedTeams, setHasInitializedTeams] = useState(false);
  const [previousTeamSelection, setPreviousTeamSelection] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [currentUserAccessLevel, setCurrentUserAccessLevel] = useState(null);
  const [shouldShowDropdown, setShouldShowDropdown] = useState(false);
  const [remountCounter, setRemountCounter] = useState(0);
  const forceComponentsRemount = useCallback(() => {
    setRemountCounter((prev) => prev + 1);
  }, []);

  const tabRemountKey = useMemo(() => {
    return `tabs-${remountCounter}-${isMeMode ? 'me' : 'team'}-${selectedTeamIds.join(',')}-${projectId}`;
  }, [remountCounter, isMeMode, selectedTeamIds, projectId]);

  const shouldShowTeamDropdown = useCallback(() => {
    if (
      availableTeams?.length === 1 &&
      currentUserAccessLevel === ACCESS_LEVEL.TEAM
    ) {
      return false;
    }
    if (
      availableTeams?.length === 1 &&
      currentUserAccessLevel === ACCESS_LEVEL.PROJECT
    ) {
      return true;
    }
    if (availableTeams?.length > 1) {
      return true;
    }
    if (currentUserAccessLevel !== null && availableTeams?.length === 0) {
      return true;
    }
    return false;
  }, [availableTeams?.length, currentUserAccessLevel, isMeMode]);

  // Effect to control dropdown visibility and prevent flickering
  useEffect(() => {
    // Only update visibility once we have both required pieces of data
    if (currentUserAccessLevel !== null && hasInitializedTeams) {
      const shouldShow = shouldShowTeamDropdown();
      setShouldShowDropdown(shouldShow);
    }
  }, [currentUserAccessLevel, hasInitializedTeams, shouldShowTeamDropdown]);
  const tenantUser = getTenantUser();
  const hasTargetAccess = includes(
    tenantUser?.tenant?.addOn,
    ADDONS?.TARGET_ACTUAL_METRIC,
  );

  const redirectTab = (tabKey) => {
    navigate(`${ROUTES.PROJECTS}/${projectId}/${tabKey}`);
  };

  const RedirectConditions = ({ tab: tabName }) => {
    switch (tabName) {
      case TAB_KEYS.EQC:
        if (!HasAccess({ type: ACCESS_TYPE.INSPECTION })) {
          if (!HasAccess({ type: ACCESS_TYPE.INSTRUCTION })) {
            redirectTab(TAB_KEYS.REGISTER);
          } else {
            redirectTab(TAB_KEYS.INSTRUCTION);
          }
        }
        break;
      case TAB_KEYS.INSTRUCTION:
        if (!HasAccess({ type: ACCESS_TYPE.INSTRUCTION })) {
          if (!HasAccess({ type: ACCESS_TYPE.INSPECTION })) {
            redirectTab(TAB_KEYS.REGISTER);
          } else {
            redirectTab(TAB_KEYS.EQC);
          }
        }
        break;
      // Commented for future use
      // case TAB_KEYS.REGISTER:
      //   if (!HasAccess({ type: ACCESS_TYPE.REGISTER })) {
      //     redirectTab(TAB_KEYS.EQC);
      //   }
      //   break;
      case TAB_KEYS.TARGET:
        if (!(HasAccess({ type: ACCESS_TYPE.INSPECTION }) && hasTargetAccess)) {
          redirectTab(TAB_KEYS.EQC);
        }
        break;
      default:
        break;
    }
    return null;
  };

  useEffect(() => {
    const unlisten = history.listen((props) => {
      if (props.action === 'POP') {
        navigate(props.location.pathname, {
          state: { navFilter, navPagination },
        });
      }
    });
    return () => {
      if (unlisten) {
        unlisten();
      }
    };
  }, [navigate, navFilter, navPagination]);

  const [getAllTeams] = useLazyQueryWithCallbacks(
    MY_PROJECT_AGENCY_DROPDOWN_LIST,
    {
      fetchPolicy: 'network-only',
      onCompleted: (teamData) => {
        if (teamData?.myProjectAgencyDropdownList?.data) {
          setAvailableTeams(teamData?.myProjectAgencyDropdownList?.data);
          const validTeamIds =
            teamData?.myProjectAgencyDropdownList?.data?.map(
              (item) => item?.id,
            ) || [];

          if (!hasInitializedTeams) {
            const migrated = migrateToProjectSpecificStorage(projectId);
            const isProjectInitialized = hasProjectBeenInitialized(projectId);
            const storedTeamFilter = getProjectTeamFilter(projectId);
            let teamsToValidate = [];

            if (migrated?.teamFilter) {
              teamsToValidate = migrated.teamFilter;
            } else if (isProjectInitialized && storedTeamFilter !== null) {
              teamsToValidate = storedTeamFilter;
            } else {
              teamsToValidate = [];
            }

            const validTeamsFromStorage =
              teamsToValidate?.filter((teamId) =>
                validTeamIds?.includes(teamId),
              ) || [];

            if (
              teamsToValidate?.length > 0 &&
              validTeamsFromStorage?.length !== teamsToValidate?.length
            ) {
              setProjectTeamFilter(projectId, validTeamsFromStorage);
            }

            if (currentUserAccessLevel === ACCESS_LEVEL.TEAM) {
              const teamsToSelect =
                validTeamsFromStorage?.length > 0
                  ? validTeamsFromStorage
                  : validTeamIds;
              setSelectedTeamIds(teamsToSelect);
              setProjectTeamFilter(projectId, teamsToSelect);
            } else {
              setSelectedTeamIds(validTeamsFromStorage);
              setProjectTeamFilter(projectId, validTeamsFromStorage);
            }
            setHasInitializedTeams(true);
          } else {
            const currentSelection = selectedTeamIds || [];
            const validCurrentSelection =
              currentSelection?.filter((teamId) =>
                validTeamIds?.includes(teamId),
              ) || [];

            if (currentSelection?.length !== validCurrentSelection?.length) {
              setSelectedTeamIds(validCurrentSelection);
              setProjectTeamFilter(projectId, validCurrentSelection);
            }
          }
        }
      },
    },
  );

  useEffect(() => {
    if (!projectId) return;

    forceComponentsRemount();
  }, [projectId, forceComponentsRemount]);

  useEffect(() => {
    if (!projectId) return;

    const migrated = migrateToProjectSpecificStorage(projectId);
    const isProjectInitialized = hasProjectBeenInitialized(projectId);
    const projectMeMode = getProjectMeMode(projectId);
    const storedTeamFilter = getProjectTeamFilter(projectId);

    setIsMeMode(projectMeMode);

    if (projectMeMode) {
      setSelectedTeamIds([]);
      setProjectTeamFilter(projectId, []);
      setHasInitializedTeams(true);
      return;
    }

    if (
      migrated?.teamFilter ||
      (isProjectInitialized && storedTeamFilter !== null) ||
      !hasInitializedTeams
    ) {
      getAllTeams({
        variables: { filter: { projectId: Number(projectId) } },
      });
    }
  }, [projectId, hasInitializedTeams, getAllTeams]);

  useEffect(() => {
    if (
      !projectId ||
      isMeMode ||
      !availableTeams?.length ||
      !hasInitializedTeams
    )
      return;

    const validTeamIds = availableTeams?.map((item) => item?.id) || [];
    const currentSelection = selectedTeamIds || [];
    const validCurrentSelection =
      currentSelection?.filter((teamId) => validTeamIds?.includes(teamId)) ||
      [];

    if (currentSelection?.length !== validCurrentSelection?.length) {
      setSelectedTeamIds(validCurrentSelection);
      setProjectTeamFilter(projectId, validCurrentSelection);
    }
  }, [
    availableTeams,
    selectedTeamIds,
    hasInitializedTeams,
    projectId,
    isMeMode,
  ]);

  useEffect(() => {
    if (!projectId || isMeMode || !availableTeams?.length) return;

    // Only clear team selection if we're not coming back from navigation
    // and user has PROJECT access with no valid team selection scenario
    if (!shouldShowDropdown) {
      // Always enforce the single team selection for TEAM access users
      if (
        currentUserAccessLevel === ACCESS_LEVEL.TEAM &&
        availableTeams?.length === 1
      ) {
        // runs even when selectedTeamIds is []
        const singleTeamId = availableTeams?.[0]?.id;
        if (
          singleTeamId &&
          (selectedTeamIds?.length !== 1 ||
            selectedTeamIds?.[0] !== singleTeamId)
        ) {
          setSelectedTeamIds([singleTeamId]);
          setProjectTeamFilter(projectId, [singleTeamId]);
          forceComponentsRemount();
        }
        return;
      }
      if (!selectedTeamIds?.length) return; // ← the old guard now applies only to the remaining logic
      const isProjectInitialized = hasProjectBeenInitialized(projectId);
      const storedTeamFilter = getProjectTeamFilter(projectId);
      // If project was initialized and had stored teams, preserve them
      // Only clear if this is the first time or no stored selection exists
      if (
        !isProjectInitialized ||
        (storedTeamFilter && storedTeamFilter?.length === 0)
      ) {
        setSelectedTeamIds([]);
        setProjectTeamFilter(projectId, []);
        forceComponentsRemount();
      }
    }
  }, [
    shouldShowDropdown,
    selectedTeamIds,
    projectId,
    isMeMode,
    availableTeams,
    currentUserAccessLevel,
    forceComponentsRemount,
  ]);

  const [getWebProject, { data }] = useLazyQueryWithCallbacks(
    GET_SINGLE_PROJECT,
    {
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        const index = findIndex(
          res?.getProject?.projectUsers,
          (user) => Number(user?.userId) === Number(currentUserId),
        );
        dispatch({
          type: 'SET_PROJECT_DETAILS',
          data: JSON.stringify(res?.getProject),
        });
        if (index !== -1) {
          const loggedInUserRole =
            res?.getProject?.projectUsers?.[index]?.roles;
          const userAccessLevel = res?.getProject?.accessLevel;
          dispatch({ type: 'SET_PROJECT_ROLE', data: loggedInUserRole });
          setCurrentUserAccessLevel(userAccessLevel);
          if (!isMeMode && !hasInitializedTeams) {
            getAllTeams({
              variables: { filter: { projectId: Number(projectId) } },
            });
          }
        } else {
          dispatch({ type: 'SET_PROJECT_ROLE', data: '' });
          setCurrentUserAccessLevel(null);
        }
      },
    },
  );

  const [createProjectRFIDraft] = useMutation(CREATE_PROJECT_RFI_DRAFT, {
    onCompleted(res) {
      if (res) {
        Event(GA_EVENT.CREATE_PROJECT_RFI_DRAFT, {
          label: GA_LABEL.CREATE_PROJECT_RFI_DRAFT,
          // eslint-disable-next-line no-undef
          pathname: window?.location?.href,
          project_id: projectId,
          user_id: currentUser?.id,
          user_name: currentUser?.name,
          tenant_id: currentUser?.tenantUser?.tenant?.id,
          tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
        });
        setShowRfiActivationRequiredModal(false);
        if (!res?.createProjectRFIDraft?.draftCreatorInfo) {
          navigate(
            `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.RFI}/${res?.createProjectRFIDraft?.projectRFIId}/draft`,
          );
          setShowModal(false);
        } else {
          setShowModal(false);
          setAlreadyDraftedUserData(res?.createProjectRFIDraft);
          setConfirmationType(CONFIRMATION_TYPES?.MULTIPLE_USER);
          setShowModal(true);
        }
      }
    },
    onError() {},
  });

  const createRfi = () => {
    if (
      data?.getProject?.projectRFIs?.length > 0 &&
      data?.getProject?.isRfiActive
    ) {
      navigate(
        `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.RFI}/${
          data?.getProject?.projectRFIs?.[0]?.id
        }${
          data?.getProject?.projectRFIs?.[0]?.status ===
          CHECKLIST_STATUS_LABEL.DRAFT
            ? '/draft'
            : ''
        }`,
      );
    } else {
      createProjectRFIDraft({ variables: { data: { projectId } } });
    }
  };

  useEffect(() => {
    if (includes(values(TAB_KEYS), tab) && !isNaN(Number(projectId))) {
      getWebProject({ variables: { id: Number(projectId) } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      dispatch({ type: 'SET_PROJECT_ROLE', data: '' });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTeamCompactDisplayText = useCallback(() => {
    const selectedCount = selectedTeamIds?.length || 0;
    const totalCount = availableTeams?.length || 0;
    if (selectedCount === 0) {
      return 'Select Your Teams';
    }
    const isAllSelected = selectedCount === totalCount && totalCount > 0;
    if (isAllSelected) {
      return 'All Teams Selected';
    }
    const displayLabel = selectedCount === 1 ? 'Team' : 'Teams';
    return `${selectedCount} ${displayLabel} Selected`;
  }, [selectedTeamIds, availableTeams]);

  if (
    !includes(values(TAB_KEYS), tab) ||
    isNaN(Number(projectId)) ||
    tab === TAB_KEYS.RFI ||
    (registerId &&
      !REGEX.REGISTER_UUID?.test(registerId) &&
      tab === TAB_KEYS.REGISTER)
  ) {
    return <Error404 />;
  }

  const items = [
    ...(HasAccess({ type: ACCESS_TYPE.INSPECTION })
      ? [
          {
            key: TAB_KEYS.EQC,
            label: 'Eqc',
            children: (
              <div className="details">
                <Eqc
                  key={`${TAB_KEYS.EQC}-${tabRemountKey}`}
                  uniqueCode={data?.getProject?.uniqueCode}
                  globalTeamFilter={selectedTeamIds}
                  isActive={tab === TAB_KEYS.EQC}
                  isMeMode={isMeMode}
                />
              </div>
            ),
          },
        ]
      : []),
    ...(HasAccess({ type: ACCESS_TYPE.INSTRUCTION })
      ? [
          {
            key: TAB_KEYS.INSTRUCTION,
            label: 'Issue',
            children: (
              <div>
                <Instruction
                  key={`${TAB_KEYS.INSTRUCTION}-${tabRemountKey}`}
                  uniqueCode={data?.getProject?.uniqueCode}
                  globalTeamFilter={selectedTeamIds}
                  isActive={tab === TAB_KEYS.INSTRUCTION}
                  isMeMode={isMeMode}
                />
              </div>
            ),
          },
        ]
      : []),
    {
      key: TAB_KEYS.REGISTER,
      label: 'Register',
      children: (
        <div>
          <Register
            key={`${TAB_KEYS.REGISTER}-${tabRemountKey}`}
            globalTeamFilter={selectedTeamIds}
            isActive={tab === TAB_KEYS.REGISTER}
            isMeMode={isMeMode}
          />
        </div>
      ),
    },
    ...(HasAccess({ type: ACCESS_TYPE.INSPECTION })
      ? [
          {
            key: TAB_KEYS.CHECKLISTS,
            label: 'Checklists',
            children: (
              <div>
                <EqcType
                  key={`${TAB_KEYS.CHECKLISTS}-${tabRemountKey}`}
                  globalTeamFilter={selectedTeamIds}
                  isActive={tab === TAB_KEYS.CHECKLISTS}
                  isMeMode={isMeMode}
                />
              </div>
            ),
          },
        ]
      : []),
    {
      key: TAB_KEYS.USERS,
      label: 'User',
      children: (
        <div>
          <User
            key={`${TAB_KEYS.USERS}-${tabRemountKey}`}
            globalTeamFilter={selectedTeamIds}
            isActive={tab === TAB_KEYS.USERS}
            isMeMode={isMeMode}
          />
        </div>
      ),
    },
    {
      key: TAB_KEYS.AGENCIES,
      label: 'Teams',
      children: (
        <div>
          {includes(location?.pathname, 'unassigned') ? (
            <UnassignAgency
              key={`${TAB_KEYS.AGENCIES}-unassigned-${tabRemountKey}`}
              globalTeamFilter={selectedTeamIds}
            />
          ) : (
            <Team
              key={`${TAB_KEYS.AGENCIES}-${tabRemountKey}`}
              globalTeamFilter={selectedTeamIds}
              isActive={tab === TAB_KEYS.AGENCIES}
              isMeMode={isMeMode}
            />
          )}
        </div>
      ),
    },
    ...(HasAccess({ type: ACCESS_TYPE.INSPECTION }) && hasTargetAccess
      ? [
          {
            key: TAB_KEYS.TARGET,
            label: titleCase(TAB_KEYS.TARGET),
            children: (
              <div>
                {currentUserAccessLevel === ACCESS_LEVEL.TEAM ? (
                  <RestrictedAccess
                    message="You cannot view this data because your access is limited to a specific team. Please contact your System or Project Admin to get full project access."
                    className="target-restricted-access"
                  />
                ) : (
                  <PlanAndTrack />
                )}
              </div>
            ),
          },
        ]
      : []),
    {
      key: TAB_KEYS.NOMENCLATURE,
      label: (
        <div className="d-flex align-center">
          {titleCase(TAB_KEYS.NOMENCLATURE)}
          {data?.getProject?.nomenclatureCount > 0 &&
            tab === TAB_KEYS.NOMENCLATURE && (
              <CommonPopover
                placement="bottom"
                classNames={{ root: 'nomenclature-info' }}
                content={
                  <>
                    <h3>Nomenclature feature Guide:</h3>
                    <ol>
                      <li>
                        <b>Create:</b> System admin and Project admin can create
                        nomenclature, there are 2 options through CSV file
                        import or by adding level manually.
                      </li>
                      <li>
                        <b>Go live:</b> Once nomenclature is created, click on
                        "Go live" button. All the project users will be sent
                        notification that this feature is activated.
                      </li>
                      <li>
                        <b>Live:</b> Once live all the mobile user will see the
                        hierarchy in their mobile phone for that project.
                      </li>
                      <li>
                        <b>Editing:</b> System admin or Project admin can from
                        projects nomenclature tab and this will be reflected in
                        mobile user once application is refreshed.
                      </li>
                    </ol>
                  </>
                }
                trigger={[!isDesktopViewport ? 'click' : 'hover']}
                getPopupContainer={() =>
                  // eslint-disable-next-line no-undef
                  document.querySelector('.project-details')
                }
              >
                <InfoIcon
                  className="ml-5 nomenclature-info-button"
                  height="18px"
                  width="18px"
                  onClick={(e) => e.stopPropagation()}
                />
              </CommonPopover>
            )}
        </div>
      ),
      className: 'nomenclature-tab',
      children: (
        <div>
          <Nomenclature
            key={`${TAB_KEYS.NOMENCLATURE}-${tabRemountKey}`}
            projectData={data?.getProject}
            projectDataRefetch={getWebProject}
            globalTeamFilter={selectedTeamIds}
            isActive={tab === TAB_KEYS.NOMENCLATURE}
            isMeMode={isMeMode}
          />
        </div>
      ),
    },
  ];
  const dropdownButtonItems = [
    CanPerform({ action: ALLOWED_ACTION_KEYS.EDIT_PROJECT }) && {
      key: '1',
      label: (
        <div
          className="edit-button-project d-flex"
          onClick={() => navigate(`${ROUTES.PROJECTS}/${projectId}/edit`)}
        >
          Project
        </div>
      ),
    },
    HasAccess({ type: ACCESS_TYPE.INSPECTION }) &&
      CanPerform({
        action: ALLOWED_ACTION_KEYS.RFI_SETUP,
        type: ALLOWED_ACTION_TYPE.BOTH,
      }) && {
        key: '2',
        label: (
          <div
            className="edit-button-project-rfi d-flex mr-10"
            onClick={() => {
              if (!includes(tenantUser?.tenant?.addOn, ADDONS.RFI)) {
                setShowUpgradeModal(true);
                setFeatureType(ADDONS.RFI);
              } else if (
                includes(tenantUser?.tenant?.addOn, ADDONS.RFI) &&
                !data?.getProject?.isRfiActive
              ) {
                setShowRfiActivationRequiredModal(true);
              } else {
                createRfi();
              }
            }}
          >
            RFI
          </div>
        ),
      },
    HasAccess({ type: ACCESS_TYPE.INSPECTION }) &&
      CanPerform({
        action: ALLOWED_ACTION_KEYS.REPORT_CONFIG,
        type: ALLOWED_ACTION_TYPE.BOTH,
      }) && {
        key: '3',
        label: (
          <div
            className="edit-button-project d-flex"
            onClick={() => {
              if (!includes(tenantUser?.tenant?.addOn, ADDONS.REPORT_CONFIG)) {
                setShowUpgradeModal(true);
                setFeatureType(ADDONS.REPORT_CONFIG);
              } else {
                setShowReportConfigModal(true);
              }
            }}
          >
            EQC report
          </div>
        ),
      },
  ];

  const projectLevelFilter = () => {
    return (
      <>
        {shouldShowDropdown && (
          <Col span={!isDesktopViewport && 12}>
            <CommonDropdown
              mode="multiple"
              className={isDesktopViewport ? 'width-250' : 'width-percent-100'}
              disabled={isMeMode}
              onChange={(id) => {
                setSelectedTeamIds(id || []);
                setProjectTeamFilter(projectId, id || []);
                forceComponentsRemount();
              }}
              onClear={() => {
                forceComponentsRemount();
              }}
              callback={(res) => {
                const teamData = res?.myProjectAgencyDropdownList?.data;
                if (teamData?.length && !availableTeams?.length) {
                  setAvailableTeams(teamData);
                  const validTeamIds = teamData?.map((item) => item?.id) || [];
                  const validSelectedTeamIds =
                    selectedTeamIds?.filter((selectedId) =>
                      validTeamIds?.includes(selectedId),
                    ) || [];
                  if (
                    selectedTeamIds?.length !== validSelectedTeamIds?.length
                  ) {
                    setSelectedTeamIds(validSelectedTeamIds);
                    setProjectTeamFilter(projectId, validSelectedTeamIds);
                    forceComponentsRemount();
                  }
                }
              }}
              value={selectedTeamIds}
              query={MY_PROJECT_AGENCY_DROPDOWN_LIST}
              fetchPolicy="network-only"
              responsePath="myProjectAgencyDropdownList.data"
              valuePath="id"
              labelPath="agency.name"
              optionKey="Team"
              variables={{ filter: { projectId: Number(projectId) } }}
              showSearch
              optionFilterProp="children"
              dropdownMatchSelectWidth={false}
              compactDisplay
              compactLabel="Teams"
              compactIcon={<TeamOutlined />}
              maxTagCount={0}
              maxTagPlaceholder={getTeamCompactDisplayText}
            />
          </Col>
        )}
        <Col span={!isDesktopViewport && 12}>
          {isMeMode && (
            <Tooltip title="View only the specific EQC, Instruction, or Register where you have performed actions.">
              <Tag
                closable
                icon={<UserOutlined />}
                onClose={() => {
                  setIsMeMode(false);
                  setProjectMeMode(projectId, false);
                  if (!shouldShowDropdown) {
                    // Single team TEAM access - auto-select the single team
                    if (
                      currentUserAccessLevel === ACCESS_LEVEL.TEAM &&
                      availableTeams?.length === 1
                    ) {
                      const singleTeamId = availableTeams?.[0]?.id;
                      setSelectedTeamIds([singleTeamId]);
                      setProjectTeamFilter(projectId, [singleTeamId]);
                    } else {
                      setSelectedTeamIds([]);
                      setProjectTeamFilter(projectId, []);
                    }
                    setPreviousTeamSelection([]);
                    forceComponentsRemount();
                    return;
                  }

                  if (previousTeamSelection?.length > 0) {
                    const validPreviousTeamIds =
                      previousTeamSelection?.filter((teamId) =>
                        availableTeams?.some((team) => team?.id === teamId),
                      ) || [];
                    setSelectedTeamIds(validPreviousTeamIds);
                    setProjectTeamFilter(projectId, validPreviousTeamIds);
                  } else {
                    const storedTeamFilter = getProjectTeamFilter(projectId);
                    const isProjectInitialized =
                      hasProjectBeenInitialized(projectId);

                    if (
                      isProjectInitialized &&
                      storedTeamFilter !== null &&
                      storedTeamFilter?.length > 0
                    ) {
                      const validStoredTeamIds =
                        storedTeamFilter?.filter((teamId) =>
                          availableTeams?.some((team) => team?.id === teamId),
                        ) || [];
                      setSelectedTeamIds(validStoredTeamIds);
                      setProjectTeamFilter(projectId, validStoredTeamIds);
                    } else {
                      getAllTeams({
                        variables: { filter: { projectId: Number(projectId) } },
                      }).then((result) => {
                        if (result?.data?.myProjectAgencyDropdownList?.data) {
                          const allTeamIds =
                            result.data.myProjectAgencyDropdownList.data.map(
                              (item) => item.id,
                            );
                          if (currentUserAccessLevel === ACCESS_LEVEL.TEAM) {
                            setSelectedTeamIds(allTeamIds);
                            setProjectTeamFilter(projectId, allTeamIds);
                          } else {
                            setSelectedTeamIds([]);
                            setProjectTeamFilter(projectId, []);
                          }
                        }
                      });
                    }
                  }
                  setPreviousTeamSelection([]);
                  forceComponentsRemount();
                }}
                onClick={() => {
                  setIsMeMode(false);
                  setProjectMeMode(projectId, false);
                  if (!shouldShowDropdown) {
                    // Single team TEAM access - auto-select the single team
                    if (
                      currentUserAccessLevel === ACCESS_LEVEL.TEAM &&
                      availableTeams?.length === 1
                    ) {
                      const singleTeamId = availableTeams?.[0]?.id;
                      setSelectedTeamIds([singleTeamId]);
                      setProjectTeamFilter(projectId, [singleTeamId]);
                    } else {
                      setSelectedTeamIds([]);
                      setProjectTeamFilter(projectId, []);
                    }
                    setPreviousTeamSelection([]);
                    forceComponentsRemount();
                    return;
                  }

                  if (previousTeamSelection?.length > 0) {
                    const validPreviousTeamIds =
                      previousTeamSelection?.filter((teamId) =>
                        availableTeams?.some((team) => team?.id === teamId),
                      ) || [];
                    setSelectedTeamIds(validPreviousTeamIds);
                    setProjectTeamFilter(projectId, validPreviousTeamIds);
                  } else {
                    const storedTeamFilter = getProjectTeamFilter(projectId);
                    const isProjectInitialized =
                      hasProjectBeenInitialized(projectId);

                    if (
                      isProjectInitialized &&
                      storedTeamFilter !== null &&
                      storedTeamFilter?.length > 0
                    ) {
                      const validStoredTeamIds =
                        storedTeamFilter?.filter((teamId) =>
                          availableTeams?.some((team) => team?.id === teamId),
                        ) || [];
                      setSelectedTeamIds(validStoredTeamIds);
                      setProjectTeamFilter(projectId, validStoredTeamIds);
                    } else {
                      getAllTeams({
                        variables: { filter: { projectId: Number(projectId) } },
                      }).then((result) => {
                        if (result?.data?.myProjectAgencyDropdownList?.data) {
                          const allTeamIds =
                            result.data.myProjectAgencyDropdownList.data.map(
                              (item) => item.id,
                            );
                          if (currentUserAccessLevel === ACCESS_LEVEL.TEAM) {
                            setSelectedTeamIds(allTeamIds);
                            setProjectTeamFilter(projectId, allTeamIds);
                          } else {
                            setSelectedTeamIds([]);
                            setProjectTeamFilter(projectId, []);
                          }
                        }
                      });
                    }
                  }
                  setPreviousTeamSelection([]);
                  forceComponentsRemount();
                }}
                className={clsx(
                  'me-mode-tag',
                  !isDesktopViewport && 'width-percent-100',
                )}
              >
                My Activities
              </Tag>
            </Tooltip>
          )}
          {!isMeMode && (
            <Tooltip title="View only the specific EQC, Instruction, or Register where you have performed actions.">
              <Button
                type="default"
                className={clsx(
                  'me-mode-toggle',
                  !isDesktopViewport && 'width-percent-100',
                )}
                onClick={() => {
                  setPreviousTeamSelection([...selectedTeamIds]);
                  setIsMeMode(true);
                  setProjectMeMode(projectId, true);
                  setSelectedTeamIds([]);
                  setProjectTeamFilter(projectId, []);
                  forceComponentsRemount();
                }}
                icon={<UserOutlined />}
              >
                My Activities
              </Button>
            </Tooltip>
          )}
        </Col>
      </>
    );
  };

  return (
    <>
      <div className="project-details">
        <div className="project-details-title d-flex justify-between align-center page-header">
          <div className="project-details-title-text d-flex">
            <ProjectIcon
              onClick={() => {
                navigate(ROUTES.PROJECTS, {
                  state: { navFilter, navPagination },
                });
              }}
              className="pointer"
            />
            <ForwardArrow />
            <div>
              <h1 className="mr-10 text-break">{data?.getProject?.name}</h1>
              <div className="d-flex align-center">
                {data?.getProject?.timeZone && (
                  <div className="d-flex align-center timezone-div">
                    <Timezone title="timeZone" />
                    {data?.getProject?.timeZone}
                  </div>
                )}
                {data?.getProject?.accessLevel && (
                  <Tag className="active-tag ml-5">
                    {`${titleCase(ACCESS_LEVEL[data?.getProject?.accessLevel])} Access`}
                  </Tag>
                )}
              </div>
            </div>
          </div>
          <div
            className={clsx(isDesktopViewport && 'project-level-filter d-flex')}
          >
            <Row
              className="d-flex filter-search align-center justify-end"
              gutter={8}
            >
              {isDesktopViewport && projectLevelFilter()}
              {CanPerform({
                action: ALLOWED_ACTION_KEYS.PROJECT_SETTING,
                type: ALLOWED_ACTION_TYPE.BOTH,
              }) && (
                <Col>
                  <Dropdown
                    menu={{ items: dropdownButtonItems }}
                    trigger={['click']}
                    placement="bottomRight"
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  >
                    <Button
                      shape="round"
                      type="primary"
                      icon={<SettingOutlined />}
                      className="setting-button fw-medium d-flex align-center"
                    >
                      Setting
                    </Button>
                  </Dropdown>
                </Col>
              )}
            </Row>
          </div>
        </div>
        <div className="d-flex align-center project-level-filter">
          {!isDesktopViewport && projectLevelFilter()}
        </div>
        <div className="mobile-card-wrapper">
          <div className="details-content">
            <RedirectConditions tab={tab} />
            <Tabs
              onTabClick={(key) => {
                getWebProject({ variables: { id: Number(projectId) } });
                navigate(`${ROUTES.PROJECTS}/${projectId}/${key}`, {
                  state: { navFilter, navPagination },
                });
              }}
              activeKey={tab}
              items={items}
              destroyOnHidden
              key={tabRemountKey}
            />
          </div>
        </div>
      </div>
      <div id="tabs-footer" />
      {showReportConfigModal && (
        <ReportConfigModal
          setShowModal={setShowReportConfigModal}
          showModal={showReportConfigModal}
          projectId={projectId}
          projectData={data?.getProject}
        />
      )}
      {showUpgradeModal && (
        <UpgradeModal
          showModal={showUpgradeModal}
          setShowModal={setShowUpgradeModal}
          projectId={projectId}
          featureKey={featureType}
          isAlreadyRequested={
            data?.getProject?.[
              featureType === REQUEST_FEATURE_UPGRADE_KEYS.RFI
                ? 'isRfiRequestSent'
                : 'isReportConfigRequestSent'
            ]
          }
          refetch={() => {
            getWebProject({ variables: { id: Number(projectId) } });
          }}
        />
      )}
      {showRfiActivationRequiredModal && (
        <RfiActivationRequiredModal
          showModal={showRfiActivationRequiredModal}
          setShowModal={setShowRfiActivationRequiredModal}
          onOk={() => {
            createRfi();
          }}
        />
      )}
      {showModal && (
        <ConfirmationModal
          type={confirmationType}
          showModal={showModal}
          setShowModal={setShowModal}
          handleConfirmation={() => {
            createProjectRFIDraft({
              variables: {
                data: {
                  projectId,
                  discardDraft: !!alreadyDraftedUserData?.draftCreatorInfo,
                },
              },
            });
          }}
          alreadyDraftedUserData={alreadyDraftedUserData}
          setConfirmationType={setConfirmationType}
          setIsActionLoading={setIsActionLoading}
          isActionLoading={isActionLoading}
          setAlreadyDraftedUserData={setAlreadyDraftedUserData}
        />
      )}
    </>
  );
};

export default ProjectDetails;
